import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { funnelApi, type FunnelAnalysisParams } from '@/api/analysis';
import { Select, type SelectOption } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Filter,
  Plus,
  RefreshCw,
  Trash2,
  Edit2,
  X,
  ChevronDown,
  TrendingDown,
  ArrowRight,
} from 'lucide-react';
import type {
  FunnelDefinition,
  FunnelDefinitionCreate,
  FunnelStepDefinition,
  FunnelAnalysisResponse,
} from '@/types/data';

// ステップ入力コンポーネント
interface StepInputProps {
  step: FunnelStepDefinition;
  index: number;
  onUpdate: (index: number, step: FunnelStepDefinition) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}

function StepInput({ step, index, onUpdate, onRemove, canRemove }: StepInputProps) {
  return (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-primary text-white rounded-full text-sm font-medium">
        {index + 1}
      </div>
      <div className="flex-1 space-y-2">
        <input
          type="text"
          value={step.name}
          onChange={(e) => onUpdate(index, { ...step, name: e.target.value })}
          placeholder="ステップ名（例: 登録）"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
        />
        <input
          type="text"
          value={step.label_patterns.join(', ')}
          onChange={(e) =>
            onUpdate(index, {
              ...step,
              label_patterns: e.target.value.split(',').map((p) => p.trim()).filter(Boolean),
            })
          }
          placeholder="ラベルパターン（カンマ区切り、例: 送信済, 登録完了）"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
        />
      </div>
      {canRemove && (
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="flex-shrink-0 p-2 text-gray-400 hover:text-red-500 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// ファネル作成/編集モーダル
interface FunnelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: FunnelDefinitionCreate) => Promise<void>;
  initialData?: FunnelDefinition;
  companyId: number;
}

function FunnelModal({ isOpen, onClose, onSave, initialData, companyId }: FunnelModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState<FunnelStepDefinition[]>([
    { name: '', label_patterns: [], order: 1 },
    { name: '', label_patterns: [], order: 2 },
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDescription(initialData.description || '');
      setSteps(initialData.steps);
    } else {
      setName('');
      setDescription('');
      setSteps([
        { name: '', label_patterns: [], order: 1 },
        { name: '', label_patterns: [], order: 2 },
      ]);
    }
    setError(null);
  }, [initialData, isOpen]);

  const handleAddStep = () => {
    setSteps([...steps, { name: '', label_patterns: [], order: steps.length + 1 }]);
  };

  const handleUpdateStep = (index: number, step: FunnelStepDefinition) => {
    const newSteps = [...steps];
    newSteps[index] = { ...step, order: index + 1 };
    setSteps(newSteps);
  };

  const handleRemoveStep = (index: number) => {
    const newSteps = steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i + 1 }));
    setSteps(newSteps);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('ファネル名を入力してください');
      return;
    }

    const validSteps = steps.filter((s) => s.name.trim() && s.label_patterns.length > 0);
    if (validSteps.length < 2) {
      setError('2つ以上のステップを定義してください（各ステップにはラベルパターンが必要です）');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        company_id: companyId,
        name: name.trim(),
        description: description.trim() || undefined,
        steps: validSteps.map((s, i) => ({ ...s, order: i + 1 })),
      });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              {initialData ? 'ファネル定義を編集' : '新しいファネルを作成'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ファネル名 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="例: 新規顧客獲得ファネル"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">説明</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="ファネルの説明（任意）"
                rows={2}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                ステップ定義 <span className="text-red-500">*</span>
              </label>
              <div className="space-y-3">
                {steps.map((step, index) => (
                  <StepInput
                    key={index}
                    step={step}
                    index={index}
                    onUpdate={handleUpdateStep}
                    onRemove={handleRemoveStep}
                    canRemove={steps.length > 2}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={handleAddStep}
                className="mt-3 flex items-center gap-2 text-sm text-primary hover:text-primary-dark transition-colors"
              >
                <Plus className="w-4 h-4" />
                ステップを追加
              </button>
            </div>
          </div>

          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              キャンセル
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  保存中...
                </>
              ) : (
                '保存'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ファネルグラフコンポーネント
interface FunnelGraphProps {
  data: FunnelAnalysisResponse;
}

function FunnelGraph({ data }: FunnelGraphProps) {
  const maxCount = Math.max(...data.steps.map((s) => s.count), 1);

  // 最大離脱ポイントを見つける
  const maxDropOffIndex = data.steps.reduce(
    (maxIdx, step, idx) => {
      if (idx === 0) return maxIdx;
      const currentDropOff = step.drop_off_count || 0;
      const maxDropOff = data.steps[maxIdx]?.drop_off_count || 0;
      return currentDropOff > maxDropOff ? idx : maxIdx;
    },
    1
  );

  return (
    <div className="space-y-4">
      {data.steps.map((step, index) => {
        const widthPercent = (step.count / maxCount) * 100;
        const isMaxDropOff = index === maxDropOffIndex && (step.drop_off_rate || 0) > 0;

        return (
          <div key={index} className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">{step.step_name}</span>
                {index > 0 && step.conversion_rate !== null && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      isMaxDropOff
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {step.conversion_rate}%転換
                  </span>
                )}
              </div>
              <span className="text-lg font-semibold text-gray-900">
                {step.count.toLocaleString()}
              </span>
            </div>

            <div className="relative">
              <div className="h-10 bg-gray-100 rounded-lg overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    index === 0
                      ? 'bg-primary'
                      : index === data.steps.length - 1
                      ? 'bg-green-500'
                      : 'bg-blue-400'
                  }`}
                  style={{ width: `${Math.max(widthPercent, 2)}%` }}
                />
              </div>
            </div>

            {index > 0 && step.drop_off_count > 0 && (
              <div
                className={`flex items-center gap-1 text-sm ${
                  isMaxDropOff ? 'text-red-600 font-medium' : 'text-gray-500'
                }`}
              >
                <TrendingDown className="w-4 h-4" />
                <span>
                  離脱: {step.drop_off_count.toLocaleString()}人 ({step.drop_off_rate}%)
                </span>
                {isMaxDropOff && <span className="text-xs">(最大離脱ポイント)</span>}
              </div>
            )}
          </div>
        );
      })}

      {/* 全体転換率 */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-600">
            <span>{data.steps[0]?.step_name}</span>
            <ArrowRight className="w-4 h-4" />
            <span>{data.steps[data.steps.length - 1]?.step_name}</span>
          </div>
          <div className="text-lg font-bold text-primary">
            全体転換率: {data.overall_conversion_rate}%
          </div>
        </div>
      </div>
    </div>
  );
}

// メインコンポーネント
export default function FunnelAnalysis() {
  const { accessibleCompanies } = useAuthStore();

  // 状態管理
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  const [definitions, setDefinitions] = useState<FunnelDefinition[]>([]);
  const [selectedFunnelId, setSelectedFunnelId] = useState<number | null>(null);
  const [analysisResult, setAnalysisResult] = useState<FunnelAnalysisResponse | null>(null);

  const [loading, setLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingFunnel, setEditingFunnel] = useState<FunnelDefinition | undefined>(undefined);

  // 初回法人設定
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  // ファネル定義一覧を取得
  const fetchDefinitions = useCallback(async () => {
    if (!selectedCompanyId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await funnelApi.getDefinitions(selectedCompanyId, true);
      setDefinitions(data);

      // 選択中のファネルがなければ最初のものを選択
      if (data.length > 0 && !selectedFunnelId) {
        setSelectedFunnelId(data[0].id);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'ファネル定義の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, selectedFunnelId]);

  useEffect(() => {
    fetchDefinitions();
  }, [fetchDefinitions]);

  // ファネル分析を実行
  const runAnalysis = useCallback(async () => {
    if (!selectedFunnelId) return;

    setAnalysisLoading(true);
    setError(null);

    try {
      const params: FunnelAnalysisParams = {
        funnel_id: selectedFunnelId,
        start_date: startDate,
        end_date: endDate,
      };

      const result = await funnelApi.analyze(params);
      setAnalysisResult(result);
    } catch (err: any) {
      setError(err.response?.data?.detail || '分析の実行に失敗しました');
    } finally {
      setAnalysisLoading(false);
    }
  }, [selectedFunnelId, startDate, endDate]);

  // 法人オプション
  const companyOptions: SelectOption[] = accessibleCompanies.map((c) => ({
    value: c.id,
    label: c.display_name || c.name,
  }));

  // ファネルオプション
  const funnelOptions: SelectOption[] = definitions.map((d) => ({
    value: d.id,
    label: d.name,
  }));

  // ファネル保存
  const handleSaveFunnel = async (data: FunnelDefinitionCreate) => {
    if (editingFunnel) {
      await funnelApi.updateDefinition(editingFunnel.id, data);
    } else {
      await funnelApi.createDefinition(data);
    }
    await fetchDefinitions();
  };

  // ファネル削除
  const handleDeleteFunnel = async (id: number) => {
    if (!confirm('このファネル定義を削除しますか？')) return;

    try {
      await funnelApi.deleteDefinition(id);
      if (selectedFunnelId === id) {
        setSelectedFunnelId(null);
        setAnalysisResult(null);
      }
      await fetchDefinitions();
    } catch (err: any) {
      setError(err.response?.data?.detail || '削除に失敗しました');
    }
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ファネル分析</h1>
          <p className="text-sm text-gray-500 mt-1">
            ユーザーの行動フローを視覚化し、転換率・離脱率を分析します
          </p>
        </div>
        <Button
          onClick={() => {
            setEditingFunnel(undefined);
            setModalOpen(true);
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          新規ファネル作成
        </Button>
      </div>

      {/* フィルター */}
      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* 法人選択 */}
          {companyOptions.length > 1 ? (
            <Select
              label="法人"
              options={companyOptions}
              value={selectedCompanyId?.toString() || ''}
              onChange={(value) => {
                setSelectedCompanyId(Number(value));
                setSelectedFunnelId(null);
                setAnalysisResult(null);
              }}
            />
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">法人</label>
              <div className="px-3 py-2 bg-gray-100 rounded-md text-sm font-medium text-gray-900">
                {companyOptions[0]?.label || '-'}
              </div>
            </div>
          )}

          {/* ファネル選択 */}
          <Select
            label="ファネル"
            options={funnelOptions}
            value={selectedFunnelId?.toString() || ''}
            onChange={(value) => setSelectedFunnelId(Number(value))}
            disabled={loading || definitions.length === 0}
          />

          {/* 開始日 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">開始日</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
            />
          </div>

          {/* 終了日 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">終了日</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm"
            />
          </div>
        </div>

        {/* 実行ボタン */}
        <div className="mt-4 flex gap-2">
          <Button onClick={runAnalysis} disabled={analysisLoading || !selectedFunnelId}>
            {analysisLoading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                分析中...
              </>
            ) : (
              <>
                <Filter className="w-4 h-4 mr-2" />
                分析実行
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* エラー表示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      )}

      {/* ファネル定義一覧 */}
      {definitions.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold text-gray-900 mb-3">ファネル定義</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {definitions.map((def) => (
              <div
                key={def.id}
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedFunnelId === def.id
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedFunnelId(def.id)}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">{def.name}</h4>
                    {def.description && (
                      <p className="text-xs text-gray-500 mt-1">{def.description}</p>
                    )}
                    <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
                      {def.steps.map((step, i) => (
                        <span key={i} className="flex items-center gap-1">
                          {i > 0 && <ChevronDown className="w-3 h-3 rotate-[-90deg]" />}
                          {step.name}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingFunnel(def);
                        setModalOpen(true);
                      }}
                      className="p-1 text-gray-400 hover:text-primary transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFunnel(def.id);
                      }}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* 分析結果 */}
      {analysisResult && (
        <Card className="p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{analysisResult.funnel_name}</h2>
            <p className="text-sm text-gray-500">
              期間: {analysisResult.period.start_date} 〜 {analysisResult.period.end_date}
            </p>
          </div>
          <FunnelGraph data={analysisResult} />
        </Card>
      )}

      {/* データなし */}
      {!loading && !error && definitions.length === 0 && (
        <Card className="p-8 text-center text-gray-500">
          <Filter className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="mb-4">ファネル定義がありません</p>
          <Button
            onClick={() => {
              setEditingFunnel(undefined);
              setModalOpen(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            最初のファネルを作成
          </Button>
        </Card>
      )}

      {/* モーダル */}
      <FunnelModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingFunnel(undefined);
        }}
        onSave={handleSaveFunnel}
        initialData={editingFunnel}
        companyId={selectedCompanyId || 0}
      />
    </div>
  );
}
